import { medienArtNachName } from '@schnitt/shared';
import { api } from './api';
import { anDieReihe } from './einfuegeWarteschlange';
import { useEditor } from './store';
import { zeitText } from './zeit';

/**
 * **Dateien hochladen -- von überall her.** Bis zum 15. Oktober stand das im
 * Blatt „Medien“; seit Dateien auch mit der Maus auf die Zeitleiste fallen
 * (Sascha: „Drag&drop als Datei muss gehen!“), braucht es einen Ort, der
 * nicht an einem offenen Blatt hängt.
 *
 * Eine Datei nach der anderen, mit Absicht: ein Telefon, das vier Videos
 * zugleich über das Mobilnetz schickt, ist mit allen vieren spät fertig und
 * zeigt bis dahin nichts Brauchbares.
 *
 * Mit `kopf` kommt das Hochgeladene in den Film, sobald es aufbereitet ist,
 * ab dieser Stelle und in dieser Reihenfolge (7.156). Ohne liegt es in den
 * Medien. Zurück kommen die Kennungen dessen, was angekommen ist.
 */
export async function hochladen(dateien: readonly File[], kopf: number | null, spur: string | null = null): Promise<string[]> {
  const s = useEditor.getState();
  const project = s.project;
  if (!project || dateien.length === 0) return [];
  const hochgeladen: string[] = [];
  for (const [index, file] of dateien.entries()) {
    s.setHochladen({ name: file.name, done: index, total: dateien.length, fraction: 0 });
    try {
      const { asset } = await api.uploadAsset(project.id, file, (fraction) =>
        useEditor.getState().setHochladen({ name: file.name, done: index, total: dateien.length, fraction }),
      );
      hochgeladen.push(asset.id);
    } catch (err) {
      s.toast('error', err instanceof Error ? err.message : `„${file.name}“ konnte nicht hochgeladen werden.`);
      break;
    }
  }
  s.setHochladen(null);
  if (kopf !== null && hochgeladen.length) {
    // Erst in die Reihe, dann die Liste holen: ist eine Datei schon fertig,
    // kommt sie mit der neuen Liste sofort hinein.
    anDieReihe(hochgeladen, kopf, spur);
    await useEditor.getState().refreshAssets();
    const zeit = zeitText(kopf);
    s.toast(
      'info',
      hochgeladen.length === 1
        ? `„${dateien[0]!.name}“ wird aufbereitet und kommt dann bei ${zeit} in den Schnitt.`
        : `${hochgeladen.length} Dateien werden aufbereitet und kommen dann ab ${zeit} in den Schnitt.`,
    );
    return hochgeladen;
  }
  await useEditor.getState().refreshAssets();
  if (hochgeladen.length) {
    s.toast('success', hochgeladen.length === 1 ? `„${dateien[0]!.name}“ wird aufbereitet.` : `${hochgeladen.length} Dateien werden aufbereitet.`);
  }
  return hochgeladen;
}

/**
 * Was beim Ablegen mitkommt und taugt: Video, Ton, Bild. Ein Ordner kommt
 * als Datei ohne Art und ohne Länge an, ein PDF mit seiner Art -- beides
 * bleibt draussen, und gesagt wird es auch.
 */
export function brauchbar(liste: FileList | readonly File[] | null | undefined): { gut: File[]; weg: string[] } {
  const gut: File[] = [];
  const weg: string[] = [];
  for (const datei of liste ? [...liste] : []) {
    const art = datei.type.split('/')[0];
    if (art === 'video' || art === 'audio' || art === 'image') gut.push(datei);
    // Nach dem Namen: manche Browser kennen die Art nicht (.mxf, .caf) oder
    // nennen eine andere (`application/ogg`). Ein Ordner hat weder Art noch
    // Inhalt und bleibt draussen.
    else if (datei.size > 0 && medienArtNachName(datei.name)) gut.push(datei);
    else weg.push(datei.name);
  }
  return { gut, weg };
}

/** Kommt beim Ziehen eine Datei mit -- und nicht ein Clip, ein Text oder ein Link? */
export function ziehtDateien(e: DragEvent | React.DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}
